import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Breadcrumbs from "../../components/pageProps/Breadcrumbs";
import { resetCart } from "../../redux/rkglobaltradeSlice";
import { emptyCart } from "../../assets/images/index";
import ItemCard from "./ItemCard";
import { Helmet } from "react-helmet";

const Cart = () => {
  const dispatch = useDispatch();
  const products = useSelector((state) => state.rkglobaltradeReducer.products);
  const [totalAmt, setTotalAmt] = useState(0);
  const [shippingCharge, setShippingCharge] = useState(0);

  useEffect(() => {
    let price = 0;
    products.forEach((item) => {
      price += item.price * item.quantity;
    });
    setTotalAmt(price);
  }, [products]);

  useEffect(() => {
    if (totalAmt <= 200) setShippingCharge(30);
    else if (totalAmt <= 400) setShippingCharge(25);
    else setShippingCharge(20);
  }, [totalAmt]);

  return (
    <div className="max-w-container mx-auto px-4">
      {/* SEO Meta Tags */}
      <Helmet>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <title>Export Cart - RK Global Trader | Secure International Trading</title>
        <meta
          name="description"
          content="Manage your export cart on RK Global Trader. Add premium agriculture products, chilli, cucumber, and onions for international trade. Check out safely and efficiently."
        />
        <meta
          name="keywords"
          content="RK Global Trader, export cart, international trade, agriculture products, online export, export India, trade platform, secure checkout"
        />
        <meta name="author" content="RK Global Trader | Export & Import from India" />
        <link rel="canonical" href="https://www.rkglobaltrader.com/cart" />
      </Helmet>

      <Breadcrumbs title="Cart" />

      {products.length > 0 ? (
        <div className="pb-20">
          {/* Cart Header */}
          <div className="w-full h-20 bg-[#F5F7F7] text-primeColor hidden lgl:grid grid-cols-5 place-content-center px-6 text-lg font-titleFont font-semibold">
            <h2 className="col-span-2">Product</h2>
            <h2>Quantity</h2>
            <h2>Sub Total</h2>
          </div>

          {/* Cart Items */}
          <div className="mt-5">
            {products.map((item, index) => (
              <ItemCard key={index} item={item} />
            ))}
          </div>

          {/* Reset Cart Button */}
          <button
            onClick={() => dispatch(resetCart())}
            className="py-2 px-10 bg-red-500 text-white font-semibold uppercase mb-4 hover:bg-red-700 duration-300"
          >
            Reset Cart
          </button>

          {/* Coupon & Update Section */}
          <div className="hidden flex flex-col mdl:flex-row justify-between border py-4 px-4 items-center gap-2 mdl:gap-0">
            <div className="flex items-center gap-4">
              <input
                type="text"
                placeholder="Coupon Code"
                className="w-44 mdl:w-52 h-8 px-4 border text-primeColor text-sm outline-none border-gray-400"
              />
              <p className="text-sm mdl:text-base font-semibold">Apply Coupon</p>
            </div>
            <p className="text-lg font-semibold">Update Cart</p>
          </div>

          {/* Cart Totals & Payment Info */}
          <div className="flex flex-col mdl:flex-row justify-between py-4 px-4 items-start mdl:items-center gap-4 mdl:gap-0">
            {/* Payment & Shipping Info */}
            <div className="flex flex-col mdl:flex-row gap-6 mdl:gap-12 text-sm mdl:text-base">
              <div>
                <h2 className="font-semibold underline mb-2">Payment Terms</h2>
                <p>We offer two secure payment options for international trade:</p>
                <ul className="list-disc list-inside">
                  <li>Advance Payment</li>
                  <li>Letter of Credit (Irrevocable & Confirmed L.C)</li>
                </ul>
              </div>
              <div>
                <h2 className="font-semibold underline mb-2">Shipping & Delivery (Incoterms)</h2>
                <p>Our logistics support includes reliable shipping methods and terms:</p>
                <ul className="list-disc list-inside">
                  <li>Cost, Insurance & Freight (CIF)</li>
                  <li>Cost Paid To (CPT – Short Distance)</li>
                  <li>Carrier Insurance Paid (CIP – Long Distance)</li>
                  <li>Delivery at Place (DAP – To Importer Port)</li>
                  <li>Free On Board (FOB – Primary Incoterm for RK Global Trader)</li>
                </ul>
              </div>
            </div>

            {/* Totals Summary */}
            <div className="w-96 flex flex-col gap-4 mt-6 mdl:mt-0">
              <h1 className="text-2xl font-semibold text-right">Cart Totals</h1>
              <div className="border border-gray-400 rounded-md">
                <p className="flex justify-between py-1.5 px-4 border-b text-lg font-medium">
                  Subtotal <span className="font-semibold">${totalAmt}</span>
                </p>
                <p className="flex justify-between py-1.5 px-4 border-b text-lg font-medium">
                  Shipping <span className="font-semibold">${shippingCharge}</span>
                </p>
                <p className="flex justify-between py-1.5 px-4 text-lg font-bold">
                  Total <span>${totalAmt + shippingCharge}</span>
                </p>
              </div>
              <Link to="/enquirenow">
                <button className="w-full h-10 bg-primeColor text-white hover:bg-black duration-300 mt-2">
                  Enquire Now
                </button>
              </Link>
            </div>
          </div>
        </div>
      ) : (
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.4 }}
          className="flex flex-col mdl:flex-row justify-center items-center gap-6 pb-20 mt-10"
        >
          <img src={emptyCart} alt="Empty Cart" className="w-80 rounded-lg p-4 mx-auto" />
          <div className="max-w-[500px] p-6 bg-white rounded-md shadow-lg text-center">
            <h1 className="font-titleFont text-xl font-bold uppercase mb-2">
              Your Cart is Empty
            </h1>
            <p className="text-sm mb-4">
              Start trading high-quality agriculture products such as chilli, cucumber, and onion. Add items to your cart and proceed to secure checkout for international export.
            </p>
            <Link to="/trade">
              <button className="bg-primeColor rounded-md px-8 py-2 text-lg font-semibold text-white hover:bg-black hover:text-gray-200 duration-300">
                Continue Trading
              </button>
            </Link>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default Cart;
